/**
 * 1.7.7 — the calls of the month's calendar, the reminders and the volunteers who opt in. Every request the screens
 * make is here; an event's own presses go through `press` (./api) with the `what` named below.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AnnouncementDetail } from '@shared/announcements';
import type { BatchResult, CancelEventInput, CopyLastMonthResponse, EventEditInput, MonthRequestPreview, ReminderChoice, VolunteerMonthAction } from '@shared/event-calendar';
import type { EventInput, EventSendResult, EventView } from '@shared/events';
import { apiFetch } from '@/lib/api';
import { press } from './api';

const post = <T,>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
const monthPath = (m: string, tail: string) => `/api/events/month/${encodeURIComponent(m)}/${tail}`;

/* ---------------- one event ---------------- */

export interface EditResult {
  event: EventView;
  /** Words to show and announce ("Saved: the day. 2 reminders moved with it."). */
  message: string;
  reminders: { armed: number; moved: number; cancelled: number };
}
/** Edit an event: only what is given changes. */
export const editEvent = (eventId: string, edit: EventEditInput) => press<EditResult>(eventId, { what: 'edit', edit });
/** Cancel the event (a reason is required). Nobody is emailed by this press. */
export const cancelEventReq = (eventId: string, cancel: CancelEventInput) => press<{ event: EventView; message: string; remindersCancelled: number }>(eventId, { what: 'cancelEvent', cancel });
/** "Reminders for this event: default / off / custom". */
export const setReminders = (eventId: string, choice: ReminderChoice) => press<EditResult>(eventId, { what: 'reminders', choice });
/** Cancel one scheduled reminder. */
export const cancelReminderReq = (eventId: string, communicationId: string) => press<{ ok: boolean; message: string }>(eventId, { what: 'cancelReminder', communicationId });
/** Request volunteers. It goes out in parts: press on while `remaining` is above 0. */
export const requestVolunteersReq = (eventId: string) => press<EventSendResult>(eventId, { what: 'requestVolunteers' });

/* ---------------- the month ---------------- */

/** Copy last month: a proposal — nothing is saved by asking. */
export function useCopyLastMonth(month: string, enabled: boolean) {
  return useQuery({ queryKey: ['events-copy', month], queryFn: () => apiFetch<CopyLastMonthResponse>(monthPath(month, 'copy')), enabled, staleTime: 0, gcTime: 0 });
}
/** The month's request to volunteers, before the press. */
export function useMonthRequest(month: string, enabled: boolean) {
  return useQuery({ queryKey: ['events-month-volunteers', month], queryFn: () => apiFetch<MonthRequestPreview>(monthPath(month, 'volunteers')), enabled, staleTime: 5_000 });
}
/** Save what the office kept of the proposal. */
export const saveMonthEvents = (month: string, events: EventInput[]) => post<BatchResult>(monthPath(month, 'press'), { what: 'save', events });
/** Announce the month: makes an announcement's draft and answers with it (open `/events/announcements?draft=<id>`). */
export const announceMonthReq = (month: string) => post<{ announcement: AnnouncementDetail; events: number }>(monthPath(month, 'press'), { what: 'announce' });
/** Print the month: a PDF (base64) to open or download. */
export const printMonthReq = (month: string, lang: 'en' | 'es') => post<{ name: string; base64: string; events: number }>(monthPath(month, 'press'), { what: 'print', lang });
/** Request for the month. It goes out in parts: press on while `remaining` is above 0. */
export const requestMonthReq = (month: string) => post<EventSendResult>(monthPath(month, 'press'), { what: 'requestVolunteers' });

/* ---------------- Settings ---------------- */

/** How many reminders are scheduled now (the kill switch's confirm says the number first). */
export function useScheduledReminders(enabled = true) {
  return useQuery({ queryKey: ['events-reminders'], queryFn: () => apiFetch<{ scheduled: number; events: number }>('/api/events/reminders'), enabled, staleTime: 5_000 });
}

/* ---------------- the volunteer's month page (public) ---------------- */

export const answerMonthReq = (token: string, eventId: string, a: 'yes' | 'no') => post<VolunteerMonthAction>('/api/link/volunteer-month', { t: token, eventId, a });

/** After a press on the calendar: the list, the event and the reminder count refresh. */
export function useCalendarRefresh() {
  const qc = useQueryClient();
  return (eventId?: string) => {
    void qc.invalidateQueries({ queryKey: ['events'] });
    void qc.invalidateQueries({ queryKey: ['events-reminders'] });
    void qc.invalidateQueries({ queryKey: ['events-month-volunteers'] });
    if (eventId) void qc.invalidateQueries({ queryKey: ['event', eventId] });
  };
}
